import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IHttpRequestOptions } from '../../interfaces/http-request-option.interface';
import { CommunityInitiativeListWithInitiativeType, ICommunityInitiativeCampaignReorderDto, ICommunityInitiativeCreateDto, ICommunityInitiativeListViewModel, ICommunityInitiativeModel, IInitiativeOrderDto, IInitiativeOrderUpdateDto, ILinkedInitiativeDto } from '../../interfaces/community-initiative.interface';
import { IPagingRequestModel } from '../../interfaces/IPagingRequestModel.interface';
import { PagedResult } from '../../interfaces/IPagingResponseModel.interface';
import { HttpWrapperService } from '../http-wrapper.service';

@Injectable({
  providedIn: 'root',
})
export class CommunityInitiativeService {
  constructor(private httpWrapperService: HttpWrapperService) {}

  createInitiative(
    createDto: ICommunityInitiativeCreateDto,
  ): Observable<ICommunityInitiativeModel> {
    const url = 'CommunityInitiative';
    const options: IHttpRequestOptions = {
      url,
      body: createDto,
    };

    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: ICommunityInitiativeModel) => response));
  }

  getInitiative(initiativeId: number): Observable<ICommunityInitiativeModel | null> {
    const url = `CommunityInitiative/${initiativeId}`;
    const options: IHttpRequestOptions = { url };

    return this.httpWrapperService.getFromApiGateWay(options).pipe(
      map((response: ICommunityInitiativeModel) => response),
      catchError(() => of(null)),
    );
  }

  updateInitiative(
    initiativeId: number,
    initiative: ICommunityInitiativeModel,
  ): Observable<boolean> {
    const url = `CommunityInitiative/${initiativeId}`;
    const options: IHttpRequestOptions = {
      url,
      body: {
        ...initiative,
        id: initiativeId,
      },
    };

    return this.httpWrapperService
      .patchFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  archiveInitiative(initiativeId: number): Observable<boolean> {
    const url = `CommunityInitiative/archive/${initiativeId}`;
    const options: IHttpRequestOptions = { url };

    return this.httpWrapperService
      .patchFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  updateAccessStatus(initiativeId: number, accessStatusId: number): Observable<boolean> {
    const url = `CommunityInitiative/access-status/${initiativeId}/${accessStatusId}`;
    const options: IHttpRequestOptions = { url };

    return this.httpWrapperService
      .patchFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  linkCampaignToInitiative(
    initiativeId: number,
    campaignId: number,
  ): Observable<boolean> {
    const url = `CommunityInitiative/${initiativeId}/campaigns/${campaignId}`;
    const options: IHttpRequestOptions = { url };

    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  unlinkCampaignFromInitiative(
    initiativeId: number,
    campaignId: number,
  ): Observable<boolean> {
    const url = `CommunityInitiative/${initiativeId}/campaigns/${campaignId}`;
    const options: IHttpRequestOptions = { url };

    return this.httpWrapperService
      .deleteFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  reorderInitiativeCampaigns(
    initiativeId: number,
    reorderDto: ICommunityInitiativeCampaignReorderDto,
  ): Observable<boolean> {
    const url = `CommunityInitiative/${initiativeId}/campaigns/reorder`;
    const options: IHttpRequestOptions = {
      url,
      body: reorderDto,
    };

    return this.httpWrapperService
      .patchFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  linkCampaignsToInitiative(
    initiativeId: number,
    campaignIds: number[],
  ): Observable<boolean> {
    const url = `InitiativeCampaignLinking/initiative/${initiativeId}`;
    const options: IHttpRequestOptions = {
      url,
      body: campaignIds,
    };

    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  linkInitiativesToCampaign(
    campaignId: number,
    initiativeIds: number[],
  ): Observable<boolean> {
    const url = `InitiativeCampaignLinking/campaign/${campaignId}`;
    const options: IHttpRequestOptions = {
      url,
      body: initiativeIds,
    };

    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  getGroupedInitiatives(
    communityId: number,
    statusId: number,
  ): Observable<IInitiativeOrderDto[]> {
    const url = `CommunityInitiative/communities/${communityId}/order/${statusId}`;
    const options: IHttpRequestOptions = { url };

    return this.httpWrapperService.getFromApiGateWay(options).pipe(
      map((response: IInitiativeOrderDto[]) => response),
      catchError(() => of([])),
    );
  }

  bulkUpdateInitiativeOrder(
    communityId: number,
    updates: IInitiativeOrderUpdateDto[],
  ): Observable<boolean> {
    const url = `CommunityInitiative/communities/${communityId}/bulk-order`;
    const options: IHttpRequestOptions = {
      url,
      body: updates,
    };

    return this.httpWrapperService.patchFromApiGateWay(options).pipe(
      map((response: boolean) => response),
      catchError(() => of(false)),
    );
  }

  getInitiativesByCommunityId(
    communityId: number,
    request: IPagingRequestModel,
  ): Observable<CommunityInitiativeListWithInitiativeType> {
    const url = `CommunityInitiative/communities/${communityId}`;
    const options: IHttpRequestOptions = {
      url,
      body: request,
      disableNotification: true, // Disable notification for 401 errors when logged out
    };

    return this.httpWrapperService.postFromApiGateWay(options).pipe(
      map((response: any) => {
        const pagedResult: PagedResult<ICommunityInitiativeListViewModel> = response.pagedResult || response.result || response;
        const campaignTypePresent: Record<number, boolean> = response.campaignTypePresent || {};

        // Process coverBlobUrl for each item in the service layer for better performance
        const processedItems = pagedResult.items.map((item: ICommunityInitiativeListViewModel) => ({
          ...item,
          coverBlobUrl: this.getCoverImageUrl(item.coverBlobUrl)
        }));
        return {
          result: {
            ...pagedResult,
            items: processedItems
          },
          hasPrivateInitiatives: response.hasPrivateInitiatives
        };
      }),
      catchError((error) => {
        // Return empty result for 401 or any other errors
        console.warn('Error fetching initiatives (likely requires auth):', error);
        return of({
          result: {
            items: [],
            paging: {
              totalRecords: 0,
              pageNumber: request.PageNumber,
              pageSize: request.PageSize,
            },
          },
          hasPrivateInitiatives: false
        });
      }),
    );
  }

  // Helper method to extract cover image URL from blob JSON string (similar to campaign service)
  private getCoverImageUrl(coverBlobUrl: string): string {
    if (!coverBlobUrl) {
      return './assets/images/placeholder.png';
    }
    
    try {
      const parsed = JSON.parse(coverBlobUrl);
      // Handle array structure from API response
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0]?.BlobUrl || './assets/images/placeholder.png';
      }
      // Fallback for direct object structure
      return parsed?.BlobUrl || './assets/images/placeholder.png';
    } catch (e) {
      // If JSON parsing fails, assume it's already a direct URL
      return coverBlobUrl || './assets/images/placeholder.png';
    }
  }

  getInitiativesList(communityId: number): Observable<ILinkedInitiativeDto[]> {
    const url = `CommunityInitiative/list/${communityId}`;
    const options: IHttpRequestOptions = { url };

    return this.httpWrapperService.getFromApiGateWay(options).pipe(
      map((response: ILinkedInitiativeDto[]) => response),
      catchError(() => of([])),
    );
  }
}